import { getEffectiveValidTo } from '@/app/components/shared/ClawPassContent/utils';
import type { ClawPassType } from '@/app/types';

/** 进入「即将过期」提醒窗口的小时阈值（7 天）。banner 仅在窗口内显示。 */
export const EXPIRE_ALERT_HOURS = 168;
/** 文案单位切换阈值：< 该值显示小时，>= 显示天。 */
export const EXPIRE_HOURS_UNIT_THRESHOLD = 24;

const HOUR_MS = 1000 * 60 * 60;
const DAY_MS = HOUR_MS * 24;

export interface ClawPassExpireState {
  /** 是否处于权益生效中（has_benefit + valid_to 合法 + 未过期） */
  isActive: boolean;
  /** 是否落在 banner 提醒窗口（<= EXPIRE_ALERT_HOURS） */
  withinAlertWindow: boolean;
  /** 是否使用小时单位文案（< EXPIRE_HOURS_UNIT_THRESHOLD） */
  useHours: boolean;
  /** 剩余天数：向上取整，至少 1（仅 isActive 时有意义；否则为 0） */
  days: number;
  /** 剩余小时：向上取整，至少 1（仅 isActive 时有意义；否则为 0） */
  hours: number;
}

const INACTIVE: ClawPassExpireState = {
  isActive: false,
  withinAlertWindow: false,
  useHours: false,
  days: 0,
  hours: 0,
};

/**
 * 由 clawPass 状态与当前时间，算出「即将过期」展示决策。
 * banner（顶部）与 popover（侧栏头像悬浮卡）共用同一规则，避免两处维护。
 *
 * 仅作用于已购付费 claw_pass 用户（userVariant === 'clawPass'）；
 * trial 用户的「即将过期 / 已过期」由 PayClawPassTooltip 单独处理，避免与本组件
 * 的 banner / popover 倒计时同时展示导致提示重复。
 *
 * 到期时间走 active_benefit_periods 第一项（叠加续购后的最终到期），
 * 没有时回退顶层 valid_to。详见 getEffectiveValidTo。
 */
export const computeClawPassExpireState = (
  clawPass:
    | Pick<ClawPassType, 'has_benefit' | 'valid_to' | 'active_benefit_periods' | 'userVariant'>
    | null
    | undefined,
  nowMs: number,
): ClawPassExpireState => {
  if (clawPass?.userVariant !== 'clawPass') return INACTIVE;
  if (!clawPass?.has_benefit) return INACTIVE;
  const validTo = getEffectiveValidTo(clawPass);
  if (!validTo) return INACTIVE;
  const endMs = new Date(validTo).getTime();
  if (Number.isNaN(endMs)) return INACTIVE;
  const diffMs = endMs - nowMs;
  if (diffMs <= 0) return INACTIVE;
  const hoursLeft = diffMs / HOUR_MS;
  return {
    isActive: true,
    withinAlertWindow: hoursLeft <= EXPIRE_ALERT_HOURS,
    useHours: hoursLeft < EXPIRE_HOURS_UNIT_THRESHOLD,
    days: Math.max(1, Math.ceil(diffMs / DAY_MS)),
    hours: Math.max(1, Math.ceil(hoursLeft)),
  };
};
